import type { Place, PlaceAddress } from "../types";

const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const arabicIndicOffset = "٠".charCodeAt(0);
const latinDigitOffset = "0".charCodeAt(0);

const kindLabels: Record<string, string> = {
  "amenity:hospital": "بیمارستان",
  "amenity:pharmacy": "داروخانه",
  "amenity:school": "مدرسه",
  "amenity:university": "دانشگاه",
  "amenity:restaurant": "رستوران",
  "amenity:cafe": "کافه",
  "place:city": "شهر",
  "place:town": "شهر",
  "place:village": "روستا",
  "shop:supermarket": "فروشگاه",
};

export function toPersianDigits(value: string | number): string {
  return String(value).replace(/[0-9٠-٩]/g, (digit) => {
    const code = digit.charCodeAt(0);
    if (code >= arabicIndicOffset && code <= arabicIndicOffset + 9) {
      return persianDigits[code - arabicIndicOffset];
    }
    return persianDigits[code - latinDigitOffset];
  });
}

export function normalizePersianText(value: string | undefined): string {
  return (value ?? "")
    .replace(/\s+/g, " ")
    .replace(/\s*[,،]\s*/g, "، ")
    .trim();
}

export function humanizePlaceKind(kind: string | undefined): string {
  if (!kind) return "";
  return kindLabels[kind.toLowerCase()] ?? "";
}

export function formatPersianPlaceTitle(place: Place): string {
  const address = place.address;
  const title =
    firstText(address?.name) ||
    firstDisplaySegment(place.display_name) ||
    firstText(address?.road) ||
    firstText(address?.pedestrian) ||
    humanizePlaceKind(place.kind) ||
    "مکان";
  return toPersianDigits(normalizePersianText(title));
}

export function formatPersianAddress(place: Place): string {
  const address = place.address;
  if (!address) return fallbackDisplayName(place.display_name);

  const title = normalizePersianText(formatPersianPlaceTitle(place));
  const components = uniqueComponents([
    withHouseNumber(firstText(address.road) ?? firstText(address.pedestrian), address.house_number),
    address.neighbourhood,
    address.quarter,
    address.suburb,
    address.city_district,
    address.district,
    locality(address),
    address.state,
    countryComponent(address),
  ]).filter((component) => component !== title);

  if (components.length === 0) return fallbackDisplayName(place.display_name);
  return toPersianDigits(components.join("، "));
}

export function formatPersianPostcode(address: PlaceAddress | undefined): string {
  const postcode = normalizePersianText(address?.postcode);
  return postcode ? toPersianDigits(postcode) : "";
}

export function publicPlaceMeta(place: Place): string {
  const kind = humanizePlaceKind(place.kind);
  const coords = `${place.lat.toFixed(5)}, ${place.lon.toFixed(5)}`;
  return toPersianDigits(kind ? `${kind} · ${coords}` : coords);
}

export function renderablePlaceText(place: Place): string {
  const postcode = formatPersianPostcode(place.address);
  return [
    formatPersianPlaceTitle(place),
    formatPersianAddress(place),
    postcode ? `کدپستی: ${postcode}` : "",
    publicPlaceMeta(place),
  ]
    .filter(Boolean)
    .join(" ");
}

function fallbackDisplayName(displayName: string): string {
  return toPersianDigits(normalizePersianText(displayName));
}

function firstDisplaySegment(displayName: string): string | undefined {
  return firstText(displayName.split(/[،,]/)[0]);
}

function firstText(value: string | undefined): string | undefined {
  const normalized = normalizePersianText(value);
  return normalized || undefined;
}

function withHouseNumber(
  road: string | undefined,
  houseNumber: string | undefined,
): string | undefined {
  if (!road) return firstText(houseNumber);
  const number = firstText(houseNumber);
  return number ? `${road} ${number}` : road;
}

function locality(address: PlaceAddress): string | undefined {
  return firstText(address.city) ?? firstText(address.town) ?? firstText(address.village);
}

function countryComponent(address: PlaceAddress): string | undefined {
  const country = firstText(address.country);
  if (!country) return undefined;
  return address.country_code?.toLowerCase() === "ir" || country === "ایران" ? undefined : country;
}

function uniqueComponents(values: Array<string | undefined>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    const value = normalizePersianText(raw);
    if (!value) continue;
    const key = value.replace(/^(شهرستان|بخش|استان)\s+/, "");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(toPersianDigits(value));
  }
  return out;
}
